import type { GameStore } from '../store/GameStore';

/**
 * Toast notification manager.
 * Subscribes to game events and shows temporary notification messages.
 * One toast visible at a time, queued FIFO.
 */
export class ToastManager {
  private container: HTMLElement;
  private queue: { message: string; type: 'info' | 'success' | 'warning' | 'urgent' }[] = [];
  private activeToast: HTMLDivElement | null = null;
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribers: (() => void)[] = [];

  constructor(container: HTMLElement, store: GameStore) {
    this.container = container;

    // Subscribe to key game events
    this.unsubscribers.push(
      store.on('STAFF_LEVELED_UP', (e) => {
        if (e.type === 'STAFF_LEVELED_UP') {
          this.show(`${e.name} leveled up to Lv.${e.newLevel}!`, 'success');
        }
      }),
      store.on('PROJECT_COMPLETED', (e) => {
        if (e.type === 'PROJECT_COMPLETED') {
          this.show(`${e.name} is ready for release!`, 'info');
        }
      }),
      // REVIEW_SCORES_GENERATED is handled by ReviewRevealDialog
      store.on('STAFF_DEMAND', (e) => {
        if (e.type === 'STAFF_DEMAND') {
          this.show(`${e.name} has a request!`, 'urgent');
        }
      }),
      store.on('STAFF_LEFT', (e) => {
        if (e.type === 'STAFF_LEFT') {
          this.show(`${e.name} has left the company.`, 'warning');
        }
      }),
      store.on('LOW_FUNDS_WARNING', (e) => {
        if (e.type === 'LOW_FUNDS_WARNING') {
          this.show(`Low funds! Only ${e.weeksOfRunway} weeks of runway.`, 'urgent');
        }
      }),
      store.on('UNLOCK_ACHIEVED', (e) => {
        if (e.type === 'UNLOCK_ACHIEVED') {
          this.show(e.description, 'success');
        }
      }),
      store.on('COMPANY_LEVELED_UP', (e) => {
        if (e.type === 'COMPANY_LEVELED_UP') {
          this.show(`Company reached Level ${e.newLevel}!`, 'success');
        }
      }),
      store.on('COMPETITOR_RELEASED', (e) => {
        if (e.type === 'COMPETITOR_RELEASED') {
          this.show(`${e.competitorName} released "${e.productName}"`, 'info');
        }
      }),
      store.on('CONTRACT_COMPLETED', (e) => {
        if (e.type === 'CONTRACT_COMPLETED') {
          this.show(`Contract done! +$${e.reward} from ${e.clientName}`, 'success');
        }
      }),
      store.on('PLATFORM_LAUNCHED', (e) => {
        if (e.type === 'PLATFORM_LAUNCHED') {
          this.show(`New platform: ${e.platformName} is now available!`, 'success');
        }
      }),
      store.on('PLATFORM_RETIRED', (e) => {
        if (e.type === 'PLATFORM_RETIRED') {
          this.show(`${e.platformName} has been discontinued.`, 'warning');
        }
      }),
      store.on('ACHIEVEMENT_UNLOCKED', (e) => {
        if (e.type === 'ACHIEVEMENT_UNLOCKED') {
          this.show(`${e.icon} ${e.name}: ${e.description}`, 'success');
        }
      }),
      store.on('CANDIDATES_READY', (e) => {
        if (e.type === 'CANDIDATES_READY') {
          this.show(`${e.tier} found ${e.count} candidates!`, 'success');
        }
      }),
      store.on('GAME_OVER', () => {
        this.show('GAME OVER — Your company has gone bankrupt.', 'urgent');
      }),
    );
  }

  /** Show a toast message. Queues if one is already visible. */
  show(message: string, type: 'info' | 'success' | 'warning' | 'urgent' = 'info'): void {
    this.queue.push({ message, type });
    if (this.queue.length > 10) {
      // Drop oldest non-urgent messages
      this.queue = this.queue.filter((t) => t.type === 'urgent' || t.type === 'warning').concat(
        this.queue.filter((t) => t.type !== 'urgent' && t.type !== 'warning').slice(-5),
      );
    }
    if (!this.activeToast) {
      this.showNext();
    }
  }

  private showNext(): void {
    // Clean up any orphaned toast element first
    if (this.activeToast) {
      this.activeToast.remove();
      this.activeToast = null;
    }
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }

    if (this.queue.length === 0) return;

    const item = this.queue.shift()!;
    const toast = document.createElement('div');
    toast.className = `toast toast--${item.type}`;
    toast.textContent = item.message;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', item.type === 'urgent' ? 'assertive' : 'polite');

    toast.addEventListener('click', () => this.dismiss());

    this.container.appendChild(toast);
    this.activeToast = toast;

    const duration = item.type === 'urgent' ? 5000 : 3000;
    this.dismissTimer = setTimeout(() => this.dismiss(), duration);

    // Safety net: force-dismiss after double the duration in case timer gets lost
    setTimeout(() => {
      if (this.activeToast === toast) this.dismiss();
    }, duration * 2);
  }

  private dismiss(): void {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    if (this.activeToast) {
      this.activeToast.remove();
      this.activeToast = null;
    }
    // Show next queued toast
    if (this.queue.length > 0) {
      setTimeout(() => this.showNext(), 200);
    }
  }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.dismiss();
    this.queue = [];
  }
}
